import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { GoogleAuthButton } from "./google";
import { SuccessButton, SecondaryButton } from "../../design-system";
import { typography, borders, surfaces, textColors, interactive } from "../../design-system/tokens";
import { Checkbox, FormField, Input } from "../../design-system/components/inputs";
import { AuthNetworkErrorState } from "../../design-system/components/layouts";
import { CheckIcon, EyeIcon, EyeOffIcon } from "../../design-system/icons";
import AuthLayout from "./AuthLayout";
import { apiFetch } from "../utils/api";
import { AuthContext } from "../context/AuthContext";
import { getReferralCoupon, ingestReferralFromSearch } from "../utils/referralCoupon";

const Register = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState("");
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null | true | false
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [legalAcceptanceError, setLegalAcceptanceError] = useState("");
  const [marketingEmails, setMarketingEmails] = useState(false);
  const legalAgreementRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useContext(AuthContext);

  useEffect(() => {
    ingestReferralFromSearch(location.search);
  }, [location.search]);

  const isBannedUsername = (name) => {
    const n = (name || "").toLowerCase();
    return n === "myvitae" || n === "myvitae.ai";
  };

  const generateUsernameSuggestions = (base) => {
    const b = (base || "").toLowerCase();
    const candidates = [
      `${b}123`,
      `${b}_user`,
      `${b}_portfolio`,
      `${b}1`,
      `${b}_dev`
    ];
    const seen = new Set();
    return candidates.filter((s) => {
      if (s.length < 5 || s.length > 30) return false;
      if (seen.has(s)) return false;
      seen.add(s);
      return true;
    }).slice(0, 3);
  };

  // Pre-fill email from URL parameter if present
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [location.search]);

  // Validate username for URL safety and length (min 5)
  const validateUsername = (value) => {
    const urlSafeRegex = /^[a-zA-Z0-9_-]*$/;
    if (!value) {
      setUsernameError("");
      return true;
    }
    if (!urlSafeRegex.test(value)) {
      setUsernameError("Username can only contain letters, numbers, hyphens (-), and underscores (_)");
      return false;
    }
    if (value.length < 5) {
      setUsernameError("Username must be at least 5 characters long");
      return false;
    }
    if (value.length > 30) {
      setUsernameError("Username must be 30 characters or less");
      return false;
    }
    setUsernameError("");
    return true;
  };

  // Password validation aligned with AccountSettings
  const getPasswordRequirementState = (pwd) => {
    const p = pwd || "";
    const minLength = 8;
    return {
      minLength,
      hasMinLength: p.length >= minLength,
      hasUpperCase: /[A-Z]/.test(p),
      hasLowerCase: /[a-z]/.test(p),
      hasNumbers: /\d/.test(p),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(p),
    };
  };

  const validatePassword = (pwd) => {
    const {
      minLength,
      hasMinLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
    } = getPasswordRequirementState(pwd);

    if (!hasMinLength) return `Password must be at least ${minLength} characters long`;
    if (!hasUpperCase) return "Password must contain at least one uppercase letter";
    if (!hasLowerCase) return "Password must contain at least one lowercase letter";
    if (!hasNumbers) return "Password must contain at least one number";
    if (!hasSpecialChar) return "Password must contain at least one special character";
    return null;
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    validateUsername(value);
    // Reset availability state while typing; debounced effect will re-check
    setUsernameAvailable(null);
    setUsernameSuggestions([]);
  };

  // Debounced availability check (1s) when username passes basic validation
  useEffect(() => {
    const urlSafeRegex = /^[a-zA-Z0-9_-]*$/;
    if (!username || !urlSafeRegex.test(username) || username.length < 5 || username.length > 30) {
      setCheckingAvailability(false);
      setUsernameAvailable(null);
      setUsernameSuggestions([]);
      return;
    }

    // Treat banned usernames as taken without hitting the API
    if (isBannedUsername(username)) {
      setCheckingAvailability(false);
      setUsernameAvailable(false);
      setUsernameSuggestions(generateUsernameSuggestions(username));
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setCheckingAvailability(true);
      try {
        const res = await apiFetch(`/api/auth/username-available/${encodeURIComponent(username)}/`, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal
        });
        if (res.ok) {
          const data = await res.json();
          setUsernameAvailable(!!data.available);
          setUsernameSuggestions((data.suggestions || []).slice(0, 3));
        } else {
          setUsernameAvailable(null);
          setUsernameSuggestions([]);
        }
      } catch (err) {
        setUsernameAvailable(null);
        setUsernameSuggestions([]);
      } finally {
        setCheckingAvailability(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      try { controller.abort(); } catch (_) {}
    };
  }, [username]);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    const pwdErr = validatePassword(value);
    setPasswordError(pwdErr || "");
  };

  const handleSuggestionClick = (suggestion) => {
    setUsername(suggestion);
    setUsernameError("");
    setError("");
  };

  const legalAcceptanceMessage = "You must agree to the Terms of Use and Privacy Policy before continuing.";

  const handleGoogleRegisterAttempt = () => {
    if (acceptedLegal) return true;
    setLegalAcceptanceError(legalAcceptanceMessage);
    setError("");
    setIsNetworkError(false);
    return false;
  };

  const submitRegister = async () => {
    setError("");
    setIsNetworkError(false);
    setLegalAcceptanceError("");

    // Validate username before submission
    if (!validateUsername(username)) {
      setError("Please fix the username errors before submitting");
      return;
    }
    // Validate password requirements
    const pwdErr = validatePassword(password);
    if (pwdErr) {
      setPasswordError(pwdErr);
      setError(pwdErr);
      return;
    }

    if (!acceptedLegal) {
      setLegalAcceptanceError(legalAcceptanceMessage);
      setError("");
      return;
    }

    // Block banned usernames as if taken
    if (isBannedUsername(username)) {
      const suggestions = generateUsernameSuggestions(username);
      setUsernameAvailable(false);
      setUsernameSuggestions(suggestions);
      setError(suggestions.length ? `This username is taken. Here are some suggestions: ${suggestions.join(', ')}` : 'This username is taken.');
      return;
    }

    // Re-check availability just before submitting
    try {
      const availRes = await apiFetch(`/api/auth/username-available/${encodeURIComponent(username)}/`, {
        method: 'GET',
        credentials: 'include'
      });
      if (availRes.ok) {
        const data = await availRes.json();
        if (!data.available) {
          const suggestions = (data.suggestions || []).slice(0, 3);
          setUsernameAvailable(false);
          setUsernameSuggestions(suggestions);
          setError(suggestions.length ? `This username is taken. Here are some suggestions: ${suggestions.join(', ')}` : 'This username is taken.');
          return;
        }
      }
    } catch (err) {
      // proceed to submit; backend will still validate
    }

    try {
      /* eslint-disable no-console */
      const referralCoupon = getReferralCoupon();
      const registerBody = { username, email, password, accepted_legal: acceptedLegal, marketing_emails: marketingEmails };
      if (referralCoupon?.code) {
        registerBody.ref_coupon = referralCoupon.code;
      }
      const res = await apiFetch("/api/auth/register/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(registerBody),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        console.warn("[Register] Failed to parse response JSON", parseErr);
      }

      console.log("[Register] status", res.status, data);

      if (res.ok) {
        // Hydrate client auth immediately to avoid ProtectedRoute bounce
        const serverUser = data.user || {};
        setAuth({
          userId: data.user_id || serverUser.id,
          username: data.username || serverUser.username || username,
          email: serverUser.email,
          firstName: serverUser.first_name,
          lastName: serverUser.last_name,
        });
        // Navigate directly to the user's dashboard after successful registration
        navigate(`/edit/${data.username || serverUser.username || username}`);
      } else {
        // Prefer structured conflicts from backend (HTTP 409)
        if (res.status === 409 && data && data.code === "already_exists") {
          if (data.field === "email") {
            setEmailTaken(true);
            setError("A user with this email already exists");
            return;
          }
          if (data.field === "username") {
            try {
              const availRes = await apiFetch(`/api/auth/username-available/${encodeURIComponent(username)}/`, {
                method: 'GET',
                credentials: 'include'
              });
              if (availRes.ok) {
                const a = await availRes.json();
                const suggestions = (a.suggestions || []).slice(0, 3);
                setUsernameAvailable(false);
                setUsernameSuggestions(suggestions);
                setError(suggestions.length ? `This username is taken. Here are some suggestions: ${suggestions.join(', ')}` : (data.error || `Registration failed (status ${res.status})`));
                return;
              }
            } catch (_) {}
            // Fallback if suggestions request fails
            setUsernameAvailable(false);
            setError(data.error || `Registration failed (status ${res.status})`);
            return;
          }
        }

        // Legacy string-based fallback for older backend responses
        if ((data.error || "").toLowerCase().includes("email") && (data.error || "").toLowerCase().includes("exist")) {
          setEmailTaken(true);
          setError("A user with this email already exists");
        } else if ((data.error || "").toLowerCase().includes("username") && (data.error || "").toLowerCase().includes("exist")) {
          try {
            const availRes = await apiFetch(`/api/auth/username-available/${encodeURIComponent(username)}/`, {
              method: 'GET',
              credentials: 'include'
            });
            if (availRes.ok) {
              const a = await availRes.json();
              const suggestions = (a.suggestions || []).slice(0, 3);
              setUsernameAvailable(false);
              setUsernameSuggestions(suggestions);
              setError(suggestions.length ? `This username is taken. Here are some suggestions: ${suggestions.join(', ')}` : (data.error || `Registration failed (status ${res.status})`));
              return;
            }
          } catch (_) {}
          setUsernameAvailable(false);
          setError(data.error || `Registration failed (status ${res.status})`);
        } else if ((data.error || "").toLowerCase().includes("already exists")) {
          try {
            const availRes = await apiFetch(`/api/auth/username-available/${encodeURIComponent(username)}/`, {
              method: 'GET',
              credentials: 'include'
            });
            if (availRes.ok) {
              const a = await availRes.json();
              const suggestions = (a.suggestions || []).slice(0, 3);
              setUsernameAvailable(false);
              setUsernameSuggestions(suggestions);
              setError(suggestions.length ? `This username is taken. Here are some suggestions: ${suggestions.join(', ')}` : (data.error || `Registration failed (status ${res.status})`));
              return;
            }
          } catch (_) {}
          setUsernameAvailable(false);
          setError(data.error || `Registration failed (status ${res.status})`);
        } else {
          setEmailTaken(false);
          setError(data.error || `Registration failed (status ${res.status})`);
        }
      }
    } catch (err) {
      console.error(err);
      setIsNetworkError(true);
      setError("Registration failed due to a network error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitRegister();
  };

  const shouldShowPasswordRequirements =
    isPasswordFocused || (((password || "").length > 0) && Boolean(validatePassword(password)));
  const showTopError = Boolean(error) && !legalAcceptanceError;

  const passwordReqState = getPasswordRequirementState(password);
  const passwordRequirements = [
    {
      key: "minLength",
      label: `At least ${passwordReqState.minLength} characters long`,
      met: passwordReqState.hasMinLength,
    },
    { key: "upper", label: "At least one uppercase letter (A-Z)", met: passwordReqState.hasUpperCase },
    { key: "lower", label: "At least one lowercase letter (a-z)", met: passwordReqState.hasLowerCase },
    { key: "number", label: "At least one number (0-9)", met: passwordReqState.hasNumbers },
    { key: "special", label: "At least one special character (e.g. !@#$%^&*)", met: passwordReqState.hasSpecialChar },
  ];

  // Dismiss legal callout when user clicks anywhere outside the agreement box.
  useEffect(() => {
    if (!legalAcceptanceError) return undefined;

    const handleOutsidePointerDown = (event) => {
      const container = legalAgreementRef.current;
      if (!container) return;
      if (!container.contains(event.target)) {
        setLegalAcceptanceError("");
      }
    };

    document.addEventListener("mousedown", handleOutsidePointerDown);
    document.addEventListener("touchstart", handleOutsidePointerDown);

    return () => {
      document.removeEventListener("mousedown", handleOutsidePointerDown);
      document.removeEventListener("touchstart", handleOutsidePointerDown);
    };
  }, [legalAcceptanceError]);

  return (
    <AuthLayout 
      title="Join"
      subtitle={null}
    >
      <h2 className={`${typography.h2} ${textColors.primary} mb-6 text-center`}>Create Account</h2>
      {isNetworkError ? (
        <div className="mb-4">
          <AuthNetworkErrorState
            title="Network error :("
            description="We couldn't create your account right now. Please check your connection."
            className="py-6"
          />
        </div>
      ) : showTopError ? (
        <div className="mb-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">
          {error}
        </div>
      ) : null}
      {emailTaken && (
        <div className="mb-4 text-center">
          <div className={`${typography.body} ${textColors.secondary} mb-1`}>Are you that user?</div>
          <div className="flex items-center justify-center gap-2">
            <Link to="/edit/login" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>Login</Link>
            <span className={`${typography.muted} ${textColors.muted}`}>or</span>
            <Link to="/edit/recovery" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>Recover password</Link>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField 
          label="Username" 
          htmlFor="username"
          required
        >
          <Input
            id="username"
            type="text"
            value={username}
            onChange={handleUsernameChange}
            placeholder="Choose a username"
            error={usernameError}
            required
          />
          {!usernameError && username && username.length >= 5 && (
            <div className="mt-2">
              {checkingAvailability && (
                <p className={`${typography.caption} ${textColors.tertiary}`}>Checking availability...</p>
              )}
              {!checkingAvailability && usernameAvailable === true && (
                <p className={`${typography.caption} text-green-600 dark:text-green-400`}>Username is available</p>
              )}
              {!checkingAvailability && usernameAvailable === false && (
                <div>
                  <p className={`${typography.caption} text-red-600 dark:text-red-400`}>This username is taken. Here are some suggestions:</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {usernameSuggestions.map((s) => (
                      <SecondaryButton
                        key={s}
                        type="button"
                        onClick={() => handleSuggestionClick(s)}
                        size="sm"
                        className="rounded-full px-2 py-1"
                      >
                        {s}
                      </SecondaryButton>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </FormField>
        
        <FormField 
          label="Email Address" 
          htmlFor="email"
          required
        >
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />
        </FormField>
        
        <FormField 
          label="Password" 
          htmlFor="password"
          required
        >
          <Input
            id="password"
            type={isPasswordVisible ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            onFocus={() => setIsPasswordFocused(true)}
            onBlur={() => setIsPasswordFocused(false)}
            placeholder="Create a password"
            error={passwordError}
            rightAdornment={
              <button
                type="button"
                onMouseDown={(e) => {
                  // Prevent input blur when toggling via mouse (keeps requirements panel stable)
                  e.preventDefault();
                }}
                onClick={() => setIsPasswordVisible((v) => !v)}
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                aria-pressed={isPasswordVisible}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${textColors.muted} ${textColors.hover} focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500`}
              >
                {isPasswordVisible ? <EyeOffIcon size="sm" /> : <EyeIcon size="sm" />}
              </button>
            }
            required
          />
          {shouldShowPasswordRequirements && (
            <div className={`mt-3 p-3 rounded-md border ${borders.default} ${surfaces.tertiary}`}>
              <p className={`${typography.label} ${textColors.primary} mb-1`}>Password Requirements:</p>
              <ul className={`mt-2 ${typography.caption} ${textColors.secondary} space-y-1`}>
                {passwordRequirements.map((req) => (
                  <li key={req.key} className="flex items-start gap-2">
                    <span className="mt-[2px] inline-flex h-4 w-4 items-center justify-center">
                      {req.met ? (
                        <CheckIcon size="sm" color="success" />
                      ) : (
                        <span className={`h-3 w-3 rounded-full border ${borders.strong} opacity-50`} />
                      )}
                    </span>
                    <span className={req.met ? "text-green-700 dark:text-green-300" : ""}>{req.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </FormField>

        <div
          ref={legalAgreementRef}
          className={`relative p-3 rounded-md border ${borders.default} ${surfaces.tertiary} space-y-3`}
        >
          {legalAcceptanceError && (
            <div className="absolute left-1/2 bottom-full z-30 mb-0.5 -translate-x-1/2 w-full flex justify-center">
              <div className="relative max-w-sm">
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-100 px-4 py-3 shadow-2xl dark:border-red-800 dark:bg-red-900"
                >
                  <p className={`${typography.bodySmall} text-red-800 dark:text-red-200 font-semibold leading-snug`}>
                    {legalAcceptanceError}
                  </p>
                </div>
                <div className="absolute left-1/2 top-full mt-1 -translate-x-1/2">
                  <div className="h-0 w-0 border-l-[11px] border-r-[11px] border-t-[13px] border-l-transparent border-r-transparent border-t-red-100 dark:border-t-red-900" />
                </div>
              </div>
            </div>
          )}
          <Checkbox
            id="accept_legal"
            checked={acceptedLegal}
            onChange={(e) => {
              setAcceptedLegal(e.target.checked);
              setLegalAcceptanceError("");
            }}
            size="sm"
            label={
              <>
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>
                  Privacy Policy
                </Link>
              </>
            }
          />
          <Checkbox
            id="marketing_emails"
            checked={marketingEmails}
            onChange={(e) => setMarketingEmails(e.target.checked)}
            size="sm"
            label="Send me tips and promotional emails"
            labelClassName="leading-snug"
          />
        </div>
        
        <SuccessButton
          type="submit"
          fullWidth={true}
        >
          Create Account
        </SuccessButton>
      </form>
      
      {/* Social registration options */}
      <div className="mt-6">
        <div className="flex items-center gap-4">
          <div className={`flex-1 border-t ${borders.default}`} />
          <span className={`${typography.muted} ${textColors.tertiary}`}>or</span>
          <div className={`flex-1 border-t ${borders.default}`} />
        </div>
        
        <div className="mt-6">
          <GoogleAuthButton mode="register" onClick={handleGoogleRegisterAttempt} />
        </div>
      </div>

      <div className="mt-6 text-center">
        <p className={`${typography.body} ${textColors.secondary}`}>
          Already have an account?{" "}
          <Link
            to="/edit/login"
            className={`underline font-medium transition-colors ${interactive.link} ${interactive.linkHover}`}
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Register;
